import axios from "axios";
import { syncPostHogOrganization } from "./posthogAnalytics";

const ROUTES_SKIP_SUBSCRIPTION_CHECK = [
  "/login",
  "/register",
  "/onboarding",
  "/subscription-ended",
];

let organizationUpdateListener = null;

/**
 * Register a callback to be notified whenever organization is updated (e.g. after
 * fetchAndSetOrganization or after runSubscriptionGate refreshes org). Main.js
 * uses this so its subscription gate state stays in sync.
 */
export function setOrganizationUpdateListener(fn) {
  organizationUpdateListener = fn;
}

export function clearOrganizationUpdateListener() {
  organizationUpdateListener = null;
}

function notifyOrganizationUpdate(org) {
  syncPostHogOrganization(org);
  if (organizationUpdateListener) {
    organizationUpdateListener(org);
  }
}

function getOrgIdFromOrg(org) {
  if (!org) return null;
  const raw = org.orgId ?? org.org_id;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

/** Resolve the user's primary org id from DynamoDB-shaped user.relatedOrgs. */
export function getOrgIdFromUser(user) {
  const relatedOrgs = user?.relatedOrgs?.L || [];
  const mainOrg = relatedOrgs.find((o) => o.M?.main?.BOOL === true);
  if (mainOrg) return mainOrg.M.org_id.S;
  if (relatedOrgs.length > 0) return relatedOrgs[0].M.org_id.S;
  return null;
}

function getSubStatus(org) {
  return org?.subscription_details?.M?.sub_status?.S ?? null;
}

function hasActiveLockoutOverride(org) {
  const lockoutOverride = org?.lockout_override?.M;
  return (
    lockoutOverride?.is_active?.BOOL === true &&
    lockoutOverride?.expiration?.N != null &&
    Number(lockoutOverride.expiration.N) > Math.floor(Date.now() / 1000)
  );
}

/**
 * If org appears cancelled/unpaid, verify with the backend. If still bad, redirect
 * to /subscription-ended. If actually good (e.g. user just renewed), refetch org,
 * update localStorage and listener, and do not redirect.
 * Skip redirect when on auth/subscription-ended routes.
 */
export async function runSubscriptionGate(org, options = {}) {
  const pathname = options.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const subStatus = getSubStatus(org);
  const hasOverride = hasActiveLockoutOverride(org);

  console.log("[Subscription gate] Checking subscription status", {
    pathname,
    subStatusFromOrg: subStatus,
    hasLockoutOverride: hasOverride,
  });

  if (ROUTES_SKIP_SUBSCRIPTION_CHECK.some((route) => pathname.startsWith(route))) {
    console.log("[Subscription gate] Skipping check (route is in skip list)");
    return;
  }

  const isCancelledOrUnpaid =
    subStatus === "cancelled" || subStatus === "canceled" || subStatus === "unpaid";
  if (!isCancelledOrUnpaid || hasOverride) {
    console.log("[Subscription gate] No action needed", {
      isCancelledOrUnpaid,
      hasOverride,
    });
    return;
  }

  const orgId = getOrgIdFromOrg(org);
  const subDetails = org?.subscription_details?.M;
  const customerId = subDetails?.stripe_cust_id?.S;
  if (!orgId || !customerId) {
    console.info("[Auth] Redirecting to subscription-ended: org or Stripe customer id is missing (account doesn't have valid org/subscription).");
    window.location.href = "/subscription-ended";
    return;
  }

  console.log("[Subscription gate] Verifying with backend (check-subscription)", { orgId });
  try {
    const subRes = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
      {
        organization_id: orgId,
        customer_id: customerId,
      }
    );
    const subscriptionStatus = subRes?.data?.subscriptionStatus ?? null;
    const isGood = subscriptionStatus === "active" || subscriptionStatus === "trialing";

    console.log("[Subscription gate] Backend subscription status", {
      subscriptionStatus,
      isGood,
    });

    if (isGood) {
      console.log("[Subscription gate] Subscription active/trialing, refreshing org and allowing access");
      const orgRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-organization",
        { orgId }
      );
      const updatedOrg = orgRes?.data?.organization;
      if (updatedOrg) {
        localStorage.setItem("organization", JSON.stringify(updatedOrg));
        notifyOrganizationUpdate(updatedOrg);
      }
      return;
    }
  } catch (err) {
    console.error("[Subscription gate] check-subscription failed", err?.response?.data ?? err?.message ?? err);
  }

  if (options.skipRedirect) {
    console.log("[Subscription gate] Still cancelled/unpaid but skipRedirect set, not redirecting");
    return;
  }
  console.info("[Auth] Redirecting to subscription-ended: subscription is cancelled/unpaid and backend verification confirmed.");
  window.location.href = "/subscription-ended";
}

/**
 * Re-check subscription with Stripe (check-subscription) and if active/trialing,
 * refresh org, update storage/listener, and redirect to /dashboard. Used on the
 * subscription-ended page so users who just renewed can get back in without logging out.
 * Reads org from localStorage. Returns true if we redirected, false otherwise.
 */
export async function recheckSubscriptionAndRedirectIfActive() {
  const orgJson = typeof window !== "undefined" ? localStorage.getItem("organization") : null;
  if (!orgJson) return false;
  let org;
  try {
    org = JSON.parse(orgJson);
  } catch {
    return false;
  }
  const orgId = getOrgIdFromOrg(org);
  const subDetails = org?.subscription_details?.M;
  const customerId = subDetails?.stripe_cust_id?.S;
  if (!orgId || !customerId) return false;

  console.log("[Subscription gate] Rechecking subscription (from subscription-ended page)", { orgId });
  try {
    const subRes = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
      {
        organization_id: orgId,
        customer_id: customerId,
      }
    );
    const subscriptionStatus = subRes?.data?.subscriptionStatus ?? null;
    const isGood = subscriptionStatus === "active" || subscriptionStatus === "trialing";
    console.log("[Subscription gate] Recheck result", { subscriptionStatus, isGood });

    if (!isGood) return false;

    console.log("[Subscription gate] Subscription active/trialing, refreshing org and redirecting to dashboard");
    const orgRes = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "get-organization",
      { orgId }
    );
    const updatedOrg = orgRes?.data?.organization;
    if (updatedOrg) {
      localStorage.setItem("organization", JSON.stringify(updatedOrg));
      notifyOrganizationUpdate(updatedOrg);
    }
    window.location.href = "/dashboard";
    return true;
  } catch (err) {
    console.error("[Subscription gate] recheckSubscriptionAndRedirectIfActive failed", err?.response?.data ?? err?.message ?? err);
    return false;
  }
}

/**
 * Fetch organization by id, store in localStorage, notify listener, and run the
 * subscription gate (verify cancelled/unpaid with backend before redirecting).
 * Returns the fetched org, or throws on request failure.
 */
export async function fetchAndSetOrganization(orgId) {
  const res = await axios.post(
    process.env.REACT_APP_BACKEND_URL + "get-organization",
    { orgId }
  );
  const org = res?.data?.organization;
  if (!org) {
    throw new Error("get-organization did not return organization");
  }
  localStorage.setItem("organization", JSON.stringify(org));
  notifyOrganizationUpdate(org);
  console.log("[Subscription gate] Running gate after fetchAndSetOrganization", { orgId });
  await runSubscriptionGate(org);
  return org;
}
